import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const SUPERVISOR_URL = "auth/admin/create-supervisor/";
const TEAM_LEAD_URL = "auth/admin/create-team-lead/";
const FIELD_AGENT_URL = "auth/admin/create-field-agent/";
const TELE_CALLER_URL = "auth/admin/create-tele-caller/";

export const fetchSupervisors = createAsyncThunk(
  "user/fetchSupervisors",
  async (params, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.token;
      if (!token) return;

      const response = await axios.get(SUPERVISOR_URL, {
        params,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      return response.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch supervisors"
      );
    }
  }
);

export const fetchTeamLeads = createAsyncThunk(
  "user/fetchTeamLeads",
  async (params, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.token;
      if (!token) return;

      const response = await axios.get(TEAM_LEAD_URL, {
        params,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      return response.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch team leads"
      );
    }
  }
);

export const fetchFieldAgents = createAsyncThunk(
  "user/fetchFieldAgents",
  async (params, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.token;
      if (!token) return;

      const response = await axios.get(FIELD_AGENT_URL, {
        params,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      return response.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch field agents"
      );
    }
  }
);

export const fetchTeleCallers = createAsyncThunk(
  "user/fetchTeleCallers",
  async (params, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.token;
      if (!token) return;

      const response = await axios.get(TELE_CALLER_URL, {
        params,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      return response.data?.data || [];
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch tele callers"
      );
    }
  }
);

const initialState = {
  // Supervisors
  supervisors: [],
  supervisors_loading: false,
  supervisors_error: null,

  // Team Leads
  team_leads: [],
  team_leads_loading: false,
  team_leads_error: null,

  // Field Agents
  field_agents: [],
  field_agents_loading: false,
  field_agents_error: null,

  // Tele Callers
  telecallers: [],
  telecallers_loading: false,
  telecallers_error: null,
};

const userRolesSlice = createSlice({
  name: "userRoles",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Supervisors
    builder
      .addCase(fetchSupervisors.pending, (state) => {
        state.supervisors_loading = true;
        state.supervisors_error = null;
      })
      .addCase(fetchSupervisors.fulfilled, (state, action) => {
        state.supervisors_loading = false;
        state.supervisors = action.payload || [];
      })
      .addCase(fetchSupervisors.rejected, (state, action) => {
        state.supervisors_loading = false;
        state.supervisors_error = action.payload || "Something went wrong";
        state.supervisors = [];
      });

    // Team Leads
    builder
      .addCase(fetchTeamLeads.pending, (state) => {
        state.team_leads_loading = true;
        state.team_leads_error = null;
      })
      .addCase(fetchTeamLeads.fulfilled, (state, action) => {
        state.team_leads_loading = false;
        state.team_leads = action.payload || [];
      })
      .addCase(fetchTeamLeads.rejected, (state, action) => {
        state.team_leads_loading = false;
        state.team_leads_error = action.payload || "Something went wrong";
        state.team_leads = [];
      });

    // Field Agents
    builder
      .addCase(fetchFieldAgents.pending, (state) => {
        state.field_agents_loading = true;
        state.field_agents_error = null;
      })
      .addCase(fetchFieldAgents.fulfilled, (state, action) => {
        state.field_agents_loading = false;
        state.field_agents = action.payload || [];
      })
      .addCase(fetchFieldAgents.rejected, (state, action) => {
        state.field_agents_loading = false;
        state.field_agents_error = action.payload || "Something went wrong";
        state.field_agents = [];
      });

    // Tele Callers
    builder
      .addCase(fetchTeleCallers.pending, (state) => {
        state.telecallers_loading = true;
        state.telecallers_error = null;
      })
      .addCase(fetchTeleCallers.fulfilled, (state, action) => {
        state.telecallers_loading = false;
        state.telecallers = action.payload || [];
      })
      .addCase(fetchTeleCallers.rejected, (state, action) => {
        state.telecallers_loading = false;
        state.telecallers_error = action.payload || "Something went wrong";
        state.telecallers = [];
      });
  },
});

export default userRolesSlice.reducer;
